import React, { useRef } from 'react';
import {
  mobileSearchInputAttrs,
  refocusMobileSearchInput,
  scrollSearchInputIntoView,
  stabilizeMobileSearchViewport,
  isMobileSearchSheet,
  isCoarsePointer,
  nudgeMobileViewportScaleAfterKeyboard,
} from '../../utils/mobileSearchInput';

/**
 * iOS Safari auto-zooms focused inputs below 16px — always enforce an explicit 16px on search
 * fields. `text-base`/`1rem` is NOT safe here: `html { font-size: 90% }` (src/index.css) makes
 * `1rem` compute to ~14.4px, which is still under the zoom threshold.
 */
const IOS_NO_ZOOM_INPUT_CLASS = '!text-[16px] !leading-normal';

/** Room for the left search icon (icon at left-3 + 16px glyph). !important beats .form-input padding shorthand. */
const ICON_INPUT_PADDING_CLASS = '!pl-11';

/**
 * Standard text search input for filter-in-place player lists (modals, panels, page filters).
 * Combobox pickers should use PlayerSearchInput instead.
 */
const MobileSearchField = ({
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder = 'Search…',
  className = 'form-input w-full',
  inputRef: externalRef,
  id,
  disabled = false,
  showIcon = false,
  /** Set false inside fixed bottom sheets — avoid scrollIntoView fighting keyboard layout */
  scrollOnFocus = true,
  /** Reset page scroll + sync sheet layout on focus (mobile bottom sheets) */
  stabilizeViewportOnFocus = false,
  onViewportStabilize,
  'aria-label': ariaLabel,
}) => {
  const internalRef = useRef(null);
  const inputRef = externalRef || internalRef;

  const handleChange = (e) => {
    onChange?.(e);
    refocusMobileSearchInput(inputRef);
  };

  const handleFocus = (e) => {
    if (stabilizeViewportOnFocus) {
      stabilizeMobileSearchViewport();
      onViewportStabilize?.();
    } else if (scrollOnFocus && !isCoarsePointer()) {
      // scrollIntoView fights the virtual keyboard's own layout animation on touch devices,
      // making the page feel like it's zooming/jumping — skip it there.
      scrollSearchInputIntoView(inputRef);
    }
    onFocus?.(e);
  };

  const handleBlur = (e) => {
    // Resets any residual iOS Safari zoom left over once the keyboard closes. No-ops on
    // non-mobile-sheet viewports, so it's safe to call unconditionally on every blur.
    nudgeMobileViewportScaleAfterKeyboard();
    onBlur?.(e);
  };

  const inputClassName = [className, showIcon ? ICON_INPUT_PADDING_CLASS : null, IOS_NO_ZOOM_INPUT_CLASS]
    .filter(Boolean)
    .join(' ');

  // Inline 16px beats any CSS specificity — iOS auto-zooms inputs below this threshold.
  const iosZoomGuardStyle = isMobileSearchSheet() ? { fontSize: '16px' } : undefined;

  const input = (
    <input
      ref={inputRef}
      id={id}
      type="text"
      value={value}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled}
      aria-label={ariaLabel}
      className={inputClassName}
      style={iosZoomGuardStyle}
      {...mobileSearchInputAttrs}
    />
  );

  if (!showIcon) return input;

  return (
    <div className="relative">
      {input}
      <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
        <svg className="h-4 w-4 shrink-0 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
    </div>
  );
};

export default MobileSearchField;
